/**
 * The connected half of `components/chrome/PhaseBar` — the queries, the copy, and the four
 * acts behind a chip's menu (`KLQ-20260827-002`).
 *
 * The split is the one `Menu`, `Slicer` and `ChipSelect` already make: the chrome component
 * knows how the row is drawn and nothing about what it is drawing, and this file knows the
 * domain and none of the geometry. That is what keeps the bar's copy in the `projects`
 * namespace, where a translator looking for the words of a project screen will find it,
 * rather than in `app`, where every screen family reads.
 *
 * **Two facts, never one control.** `usePhaseContext` hands back the viewer's `selectedId`
 * (URL, private, changes the screen) and the project's `currentPhaseId` (server, shared,
 * decides where the next drawing is filed). This file passes both down separately and the
 * chrome draws them in two different visual registers. The "set as current" action writes the
 * second and deliberately does **not** touch the first: an architect who moves the project's
 * default to DSP has not asked to stop looking at DÚR.
 *
 * **Who gets the pencils.** `mayEditProjectSettings(surfaces)` gates every mutating
 * affordance — the settings surface at `write` and no zone scope, the same authority
 * (`Action.update_project`, unscoped-only since `KLQ-20261028-008`) the phase endpoints check.
 * As everywhere in this app the client's check is a *rendering* decision: the server refuses
 * independently, and a wrong answer here costs a button that 403s, never access.
 *
 * **Nothing here refetches on a phase switch.** Selecting writes a search parameter; the phase
 * list is one cached request per project visit; the surfaces below filter what they already
 * hold. The only requests this file can make are the four mutations, each of which invalidates
 * exactly what it changed.
 */

import { Check, Pencil, Trash2, Undo2, Archive } from 'lucide-react';
import { useState } from 'react';

import type { MenuItem } from '@/components';
import { PhaseBar } from '@/components/chrome';
import type { PhaseChip } from '@/components/chrome';
import { useNormCatalogue } from '@/features/setup';
import { useMessages } from '@/lib/i18n';

import type { ProjectPhase } from './api';
import { findNormEdition, phaseLabel, usePhaseContext } from './phase';
import { isPhaseInUse } from './problems';
import { PhaseDrawer } from './PhaseDrawer';
import {
  useDeleteProjectPhase,
  useProject,
  useProjectSurfaces,
  useSetCurrentPhase,
  useUpdateProjectPhase,
} from './queries';
import { mayEditProjectSettings } from './scope';

type Panel =
  | { readonly kind: 'none' }
  | { readonly kind: 'add' }
  | { readonly kind: 'rename'; readonly phase: ProjectPhase };

export interface ProjectPhaseBarProps {
  readonly projectId: string;
}

export function ProjectPhaseBar({ projectId }: ProjectPhaseBarProps) {
  const t = useMessages('projects');
  const { phases, selectedId, currentPhaseId, select } = usePhaseContext(projectId);
  const project = useProject(projectId);
  const surfaces = useProjectSurfaces(projectId);
  // The catalogue is reference data held for the whole session (`queries.ts`), so this is a
  // cache read on every project after the first.
  const catalogue = useNormCatalogue();

  const setCurrent = useSetCurrentPhase(projectId);
  const update = useUpdateProjectPhase(projectId);
  const remove = useDeleteProjectPhase(projectId);

  const [panel, setPanel] = useState<Panel>({ kind: 'none' });

  const mayAdminister = mayEditProjectSettings(surfaces.data);

  const edition = findNormEdition(catalogue.data, project.data?.norm_version_id);
  const norm = edition === null ? undefined : `${edition.norm.code} · ${edition.version.label}`;

  function menuFor(phase: ProjectPhase): readonly MenuItem[] {
    if (!mayAdminister) return [];
    const archived = phase.archived_at !== null && phase.archived_at !== undefined;
    const items: MenuItem[] = [];

    // Absent rather than disabled for the rung that already is current: an item that says
    // "make this the thing it already is" teaches nobody anything. An *archived* rung is
    // absent for a different reason — archiving means not offered for new work, and current
    // is exactly "where new work lands".
    if (!archived && !phase.is_current) {
      items.push({
        id: 'current',
        label: t('phase.action.setCurrent'),
        icon: <Check size={14} strokeWidth={1.5} aria-hidden />,
        onSelect: () => {
          setCurrent.mutate(phase.id);
        },
      });
    }

    items.push({
      id: 'rename',
      label: t('phase.action.rename'),
      icon: <Pencil size={14} strokeWidth={1.5} aria-hidden />,
      onSelect: () => {
        setPanel({ kind: 'rename', phase });
      },
    });

    items.push({
      id: 'archive',
      label: archived ? t('phase.action.restore') : t('phase.action.archive'),
      icon: archived ? (
        <Undo2 size={14} strokeWidth={1.5} aria-hidden />
      ) : (
        <Archive size={14} strokeWidth={1.5} aria-hidden />
      ),
      onSelect: () => {
        update.mutate({ phaseId: phase.id, body: { archived: !archived } });
      },
    });

    items.push({ id: 'sep', separator: true });
    items.push({
      id: 'remove',
      label: t('phase.action.remove'),
      tone: 'danger',
      icon: <Trash2 size={14} strokeWidth={1.5} aria-hidden />,
      onSelect: () => {
        remove.mutate(phase.id, {
          onError: (failure) => {
            // A phase with drawings under it cannot be deleted, and that is the design rather
            // than a failure (`problems.ts`). Archiving is what the person meant, so it is
            // done — the row stays, the drawings keep their address, and the rung stops being
            // offered for new work. Anything else is a real error and stays a real error.
            if (isPhaseInUse(failure)) {
              update.mutate({ phaseId: phase.id, body: { archived: true } });
            }
          },
        });
        // The viewer was looking at the rung they just removed. Fall back to *All* rather than
        // leaving a selection pointing at nothing — `usePhaseContext` would resolve it to All
        // on the next render anyway, and doing it here means the URL stops lying immediately.
        if (phase.id === selectedId) select(null);
      },
    });

    return items;
  }

  const chips: readonly PhaseChip[] = phases.map((phase) => ({
    id: phase.id,
    label: phaseLabel(phase),
    archived: phase.archived_at !== null && phase.archived_at !== undefined,
    isCurrent: phase.id === currentPhaseId,
    menu: menuFor(phase),
  }));

  return (
    <>
      <PhaseBar
        chips={chips}
        selectedId={selectedId}
        onSelect={select}
        label={t('phase.bar.label')}
        allLabel={t('phase.all')}
        currentLabel={t('phase.current')}
        archivedLabel={t('phase.archived')}
        menuLabel={(label) => t('phase.menu.label', { phase: label })}
        {...(mayAdminister
          ? {
              addLabel: t('phase.action.add'),
              onAdd: () => {
                setPanel({ kind: 'add' });
              },
            }
          : {})}
        {...(norm === undefined ? {} : { norm })}
      />
      {panel.kind === 'none' ? null : (
        <PhaseDrawer
          projectId={projectId}
          normVersionId={project.data?.norm_version_id ?? null}
          phases={phases}
          {...(panel.kind === 'rename' ? { renaming: panel.phase } : {})}
          onClose={() => {
            setPanel({ kind: 'none' });
          }}
        />
      )}
    </>
  );
}
